import React, { Fragment } from "react";
import { DataCategoryViewModel, DataPrivacyClassification } from "../DataCategoriesStep.types";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalInformationTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useDataCategoriesStepstyles } from "../DataCategoriesStep.styles";
import DataCategoryBox from "../../components/dataCategoryBox/DataCategoryBox.tsx";
import { DataCategoryDataTypesSaveModel } from "../../ProcessingActivity.types.ts";

export interface DataCategoryPickerProps {
    processingActivityId?: string;
    isLoading: boolean;
    readOnly: boolean | undefined;
    allDataCategories: Array<DataCategoryViewModel> | undefined;
    onDataCategoryClick: (isSelected: boolean, dataCategoryDataTypes: DataCategoryDataTypesSaveModel) => void;
    disabledTooltip?: string;
    onDisabledTooltipClick?: (dataCategoryId: string) => void;
    disableDataTypeSelection?: boolean;
    dataSubjectId?: string;
    displayAllDataTypes?: boolean;
    singleLoadingCategory?: boolean;
    nonSensitiveTooltip?: string;
    sensitiveTooltip?: string;
}

function DataCategoryPicker(props: DataCategoryPickerProps) {
    const { translateString } = useTranslation();
    const styles = useDataCategoriesStepstyles();
    const gridSpacing = 3;

    const ordinaryDataCategories = (props.singleLoadingCategory ? [1] : [1, 2, 3, 4, 5, 6, 7, 8]).map((x) => {
        return {
            id: x.toString(),
            name: "",
            classification: DataPrivacyClassification.Ordinary,
            description: "",
            isSelected: false,
            dataTypeIds: [],
            selectedDataTypeIds: [],
            canSpecifyDataTypesForAllDataSubjects: false,
        };
    });

    const sensitiveDataCategories = (props.singleLoadingCategory ? [11] : [11, 12, 13, 14]).map((x) => {
        return {
            id: x.toString(),
            name: "",
            classification: DataPrivacyClassification.Sensitive,
            description: "",
            isSelected: false,
            dataTypeIds: [],
            selectedDataTypeIds: [],
            canSpecifyDataTypesForAllDataSubjects: false,
        };
    });

    const dataCategoriesLoadingData: Array<DataCategoryViewModel> = [
        ...ordinaryDataCategories,
        {
            id: "9",
            name: "",
            classification: DataPrivacyClassification.NationalIdentificationNumber,
            description: "",
            isSelected: false,
            dataTypeIds: [],
            selectedDataTypeIds: [],
            canSpecifyDataTypesForAllDataSubjects: false,
        },
        {
            id: "10",
            name: "",
            classification: DataPrivacyClassification.CriminalOffenses,
            description: "",
            isSelected: false,
            dataTypeIds: [],
            selectedDataTypeIds: [],
            canSpecifyDataTypesForAllDataSubjects: false,
        },
        ...sensitiveDataCategories,
    ];

    const data = props.isLoading ? dataCategoriesLoadingData : (props.allDataCategories ?? []);

    const getDataCategoryGridContainer = (classification: DataPrivacyClassification) => {
        return (
            <Grid container spacing={gridSpacing}>
                {data!
                    .filter((x) => x.classification === classification)
                    .map((d, index) => {
                        return (
                            <Grid key={d.id}>
                                <DataCategoryBox {...props} size={"large"} dataCategory={d} isLoading={props.isLoading} />
                            </Grid>
                        );
                    })}
            </Grid>
        );
    };

    return (
        <Fragment>
            <Fragment>
                {data!.filter((x) => x.classification === DataPrivacyClassification.Ordinary).length > 0 && (
                    <Fragment>
                        <Box sx={styles.firstTypographyContainer}>
                            <Typography sx={styles.infoHeader}>{translateString("dataCategoriOrdinary")}</Typography>
                            <Box sx={styles.toolTipContainer}>
                                <DotLegalInformationTooltip
                                    text={props.nonSensitiveTooltip ? props.nonSensitiveTooltip : translateString("nonSensitiveHelperText")}
                                />
                            </Box>
                        </Box>
                        {getDataCategoryGridContainer(DataPrivacyClassification.Ordinary)}
                    </Fragment>
                )}
            </Fragment>

            <Fragment>
                {data!.filter((x) => x.classification === DataPrivacyClassification.NationalIdentificationNumber).length > 0 && (
                    <Fragment>
                        <Box sx={styles.firstTypographyContainer}>
                            <Typography sx={styles.infoHeader}>{translateString("nationalidentificationnumber")}</Typography>
                        </Box>
                        {getDataCategoryGridContainer(DataPrivacyClassification.NationalIdentificationNumber)}
                    </Fragment>
                )}
            </Fragment>

            <Fragment>
                {data!.filter((x) => x.classification === DataPrivacyClassification.CriminalOffenses).length > 0 && (
                    <Fragment>
                        <Box sx={styles.firstTypographyContainer}>
                            <Typography sx={styles.infoHeader}>{translateString("criminaloffenses")}</Typography>
                        </Box>
                        {getDataCategoryGridContainer(DataPrivacyClassification.CriminalOffenses)}
                    </Fragment>
                )}
            </Fragment>

            <Fragment>
                {data!.filter((x) => x.classification === DataPrivacyClassification.Sensitive).length > 0 && (
                    <Fragment>
                        <Box sx={styles.firstTypographyContainer}>
                            <Typography sx={styles.infoHeader}>{translateString("dataCategoriSensitive")}</Typography>
                            <Box sx={styles.toolTipContainer}>
                                <DotLegalInformationTooltip text={props.sensitiveTooltip ? props.sensitiveTooltip : translateString("sensitiveHelperText")} />
                            </Box>
                        </Box>
                        {getDataCategoryGridContainer(DataPrivacyClassification.Sensitive)}
                    </Fragment>
                )}
            </Fragment>

            <Fragment>
                {data!.filter((x) => x.classification === DataPrivacyClassification.None).length > 0 && (
                    <Fragment>
                        <Box sx={styles.firstTypographyContainer}>
                            <Typography sx={styles.infoHeader}>{translateString("dataCategoriNone")}</Typography>
                        </Box>
                        {getDataCategoryGridContainer(DataPrivacyClassification.None)}
                    </Fragment>
                )}
            </Fragment>
        </Fragment>
    );
}

export default DataCategoryPicker;
